//deps
import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo } from "react";

//internal-deps
import {
    LoadingScreen,
    AddMoneyAndDelinkWallet as AddMoneyAndDelinkWalletUi,
    TickScreen,
    TICK_STATES,
    PaymentProgressLoader,
} from "@swiggy-private/payment-ui";
import { GenericError, useEffectOnce } from "@swiggy-private/core-ui";
import { CheckoutRoute, getBridgeSdk, closeWebview, useCustomHardwareBack } from "@swiggy-private/checkout-core";
import { ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { WALLET_CODES } from "@swiggy-private/payment-types";

//components
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper";

//defs
import { RouteProps } from "@payment-client/interfaces/Router";

// styles
import styles from "./styles.scss";

//hooks
import { useGetLinkedWalletDetails } from "@payment-client/hooks/UseGetLinkedWalletDetails";
import { useAddMoney } from "@payment-client/hooks/UseAddMoney";
import { useDelinkWallet } from "@payment-client/hooks/UseDelinkWallet";
import { useWalletAnalyticsHandler } from "@payment-client/hooks/UseWalletAnalyticsHandler";

//helpers
import {
    getAddMoneyAndDelinkWalletPageTitle,
    shouldEnableAddMoneyToWallet,
    shouldShowBalance,
    getWalletCodes,
} from "@payment-client/helpers/WalletHelper";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// types
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

//constants
import { TEXTS } from "@payment-client/constants/Wallet";
import { isEmpty } from "@swiggy-private/js-utils";

type AddMoneyAndDelinkWalletProps = RouteProps<{
    walletCode: string;
    fromWalletPage: string;
}>;

function AddMoneyAndDelinkWallet(props: AddMoneyAndDelinkWalletProps): ReactElement {
    const { data } = props;
    const walletCode = data?.walletCode as WALLET_CODES;
    const fromWalletPage = data?.fromWalletPage;

    const { paymentResponse } = useContext(ShowPaymentContext);
    const tenant = paymentResponse ? paymentResponse.tenant : undefined;

    const {
        isLoading: fetchWalletDetailsInProgress,
        retry,
        walletDetails,
    } = useGetLinkedWalletDetails(walletCode, tenant);
    const { delinkUserWallet, isDelinkSuccess, isDelinkInProgress, isDelinkFailure, resetDelinkState } =
        useDelinkWallet(walletDetails, tenant);

    const genericAlertAction = useMemo(() => ({ onClick: retry }), [retry]);

    const [addMoneyInProgress, addMoneyToWallet] = useAddMoney();

    const onAddMoney = useCallback(
        (amount: number) => {
            if (walletDetails) {
                addMoneyToWallet({
                    amount: String(amount),
                    walletCode,
                    mobile: walletDetails.mobileNumber,
                    isPaymentModeFlow: true,
                });
            }
        },
        [addMoneyToWallet, walletCode, walletDetails],
    );

    const isAddMoneyToWalletEnabled = useMemo(
        () => walletDetails?.addMoneyAllowed !== false && shouldEnableAddMoneyToWallet(walletCode),
        [walletCode, walletDetails],
    );
    const showBalance = useMemo(() => shouldShowBalance(walletCode), [walletCode]);

    const onDelinkWallet = useCallback(() => {
        delinkUserWallet(walletCode);
    }, [delinkUserWallet, walletCode]);

    const onCloseSuccessBottomSheet = useCallback(() => {
        if (walletDetails) {
            getBridgeSdk()
                .walletDelinked({
                    walletCode,
                    mobileNumber: walletDetails.mobileNumber,
                    walletCodes: getWalletCodes(walletCode),
                    ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                })
                .catch(/* istanbul ignore next */ () => void 0);
        }
    }, [tenant, walletCode, walletDetails]);

    const closeDelinkErrorBottomSheet = useCallback(() => {
        if (isDelinkFailure) {
            resetDelinkState();
        }
    }, [isDelinkFailure, resetDelinkState]);

    const pageTitle = useMemo(() => getAddMoneyAndDelinkWalletPageTitle(walletCode), [walletCode]);

    useMemo(() => {
        if (fromWalletPage === "true") {
            getBridgeSdk()
                .walletAddMoneySuccess({
                    walletCode,
                    ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                })
                .catch(/* istanbul ignore next */ () => void 0);
        }
    }, [fromWalletPage, tenant, walletCode]);

    const { delinkWalletAnalyticHandler, onAddMoneyImpressionEvent, onImpressionCurrentBalance, onScreenViewAddMoney } =
        useWalletAnalyticsHandler({
            isAddMoneyToWalletEnabled,
            walletCode,
        });

    useEffectOnce(() => {
        if (isAddMoneyToWalletEnabled) {
            onScreenViewAddMoney(walletCode);
        }
    });

    useEffect(() => {
        if (walletDetails) {
            if (isAddMoneyToWalletEnabled) {
                onAddMoneyImpressionEvent(Number(walletDetails.balance));
            }
            onImpressionCurrentBalance(Number(walletDetails.balance));
        }
    }, [isAddMoneyToWalletEnabled, onAddMoneyImpressionEvent, onImpressionCurrentBalance, walletDetails]);
    const onBack = useCallback(() => {
        if (isDelinkSuccess || isDelinkInProgress) {
            return;
        }
        closeWebview();
    }, [isDelinkInProgress, isDelinkSuccess]);

    useCustomHardwareBack([CheckoutRoute.WALLET_DETAILS], onBack);

    if (fetchWalletDetailsInProgress) {
        return (
            <PaymentModesWrapper title={pageTitle}>
                <LoadingScreen className={styles.loadingScreen} />
            </PaymentModesWrapper>
        );
    }

    if (!walletDetails) {
        return (
            <PaymentModesWrapper title={pageTitle}>
                <GenericError
                    action={genericAlertAction}
                    dataTestId={ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.GENERIC_ERROR}
                />
            </PaymentModesWrapper>
        );
    }

    if (isDelinkInProgress) {
        return (
            <TickScreen tickState={TICK_STATES.LOADING} mainText={TEXTS.HOLD_ON} subText={TEXTS.DELINK_IN_PROGRESS} />
        );
    }

    return (
        <PaymentModesWrapper
            title={pageTitle}
            containerClassName={styles.container}
            backButtonClass={styles.containerBackButton}
        >
            {addMoneyInProgress && <PaymentProgressLoader />}
            <AddMoneyAndDelinkWalletUi
                onAddMoney={onAddMoney}
                onDelinkWallet={onDelinkWallet}
                walletDetails={walletDetails}
                isDelinkSuccess={isDelinkSuccess}
                isDelinkFailure={isDelinkFailure}
                onCloseDelinkSuccessBottomSheet={onCloseSuccessBottomSheet}
                onCloseDelinkFailureBottomSheet={closeDelinkErrorBottomSheet}
                isAddMoneyToWalletEnabled={isAddMoneyToWalletEnabled}
                showBalance={showBalance}
                analyticHandlers={delinkWalletAnalyticHandler}
            />
        </PaymentModesWrapper>
    );
}

export default memo(AddMoneyAndDelinkWallet);
